import type { 
  UserProfile, 
  UpdateProfileInput, 
  FriendWithProfile, 
  FriendStats,
  FriendshipStatus 
} from '@/types';
import { apiFetch } from './apiClient';

/**
 * Get sent friend requests (outgoing pending requests)
 */
export async function getSentRequests(): Promise<FriendWithProfile[]> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/friends/sent`,
    {}
  );

  if (!response.ok) {
    throw new Error('Failed to fetch sent requests');
  }

  return response.json();
}

/**
 * Get the current user's profile
 */
export async function getProfile(): Promise<UserProfile> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/profile`,
    {}
  );

  if (!response.ok) {
    throw new Error('Failed to fetch profile');
  }

  return response.json();
}

/**
 * Update the current user's profile
 */
export async function updateProfile(input: UpdateProfileInput): Promise<UserProfile> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/profile`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to update profile');
  }

  return response.json();
}

/**
 * Search for users by email
 */
export async function searchUsers(email: string): Promise<UserProfile[]> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/users/search?email=${encodeURIComponent(email)}`,
    {}
  );

  if (!response.ok) {
    throw new Error('Failed to search users');
  }

  return response.json();
}

/**
 * Send a friend request
 */
export async function sendFriendRequest(friendId: string): Promise<void> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/friends/request`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ friendId }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to send friend request' }));
    throw new Error(errorData.error || 'Failed to send friend request');
  }
}

/**
 * Delete the current user's account and all associated data
 */
export async function deleteAccount(): Promise<void> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/account`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to delete account' }));
    throw new Error(errorData.error || 'Failed to delete account');
  }
}

/**
 * Get friends list (optionally filter by status)
 */
export async function getFriends(status?: FriendshipStatus): Promise<FriendWithProfile[]> {
  const url = status 
    ? `${import.meta.env.VITE_API_ENDPOINT}/friends?status=${status}`
    : `${import.meta.env.VITE_API_ENDPOINT}/friends`;
  
  const response = await apiFetch(url);

  if (!response.ok) {
    throw new Error('Failed to fetch friends');
  }

  return response.json();
}

/**
 * Accept a friend request
 */
export async function acceptFriendRequest(friendId: string): Promise<void> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/friends/${friendId}/accept`,
    {
      method: 'PUT',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to accept friend request');
  }
}

/**
 * Remove a friend or reject a friend request
 */
export async function removeFriend(friendId: string): Promise<void> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/friends/${friendId}`,
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to remove friend');
  }
}

/**
 * Get a friend's stats
 */
export async function getFriendStats(friendId: string): Promise<FriendStats> {
  const response = await apiFetch(
    `${import.meta.env.VITE_API_ENDPOINT}/friends/${friendId}/stats`,
    {}
  );

  if (!response.ok) {
    throw new Error('Failed to fetch friend stats');
  }

  return response.json();
}
